import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  Search,
  Plus,
  PlayCircle,
  Calendar,
  Users,
  User,
  CheckCircle2,
  Trash2,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EvaluationFreelancerCard } from "./EvaluationFreelancerCard";
import type {
  EvaluationGroupProgress,
  EvaluationGroupFreelancer,
} from "@/types/evaluation";
import { toUserError } from "@/lib/api-error";

interface EvaluationGroupDetailsProps {
  group: EvaluationGroupProgress;
  onBack: () => void;
  onAddFreelancers?: () => void;
  onDeleteGroup?: () => void;
  userProfile?: string | null;
}

export function EvaluationGroupDetails({
  group,
  onBack,
  onAddFreelancers,
  onDeleteGroup,
  userProfile,
}: EvaluationGroupDetailsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [freelancerToRemove, setFreelancerToRemove] = useState<EvaluationGroupFreelancer | null>(null);
  const [isRemovingFreelancer, setIsRemovingFreelancer] = useState(false);
  const [isUpdatingReviewStatus, setIsUpdatingReviewStatus] = useState(false);
  const [freelancerToReview, setFreelancerToReview] = useState<EvaluationGroupFreelancer | null>(null);
  const [reviewReason, setReviewReason] = useState("");
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const canAddFreelancers =
    userProfile === "Admin" || userProfile === "Membro";

  // Refetch data when component mounts or location changes to this route
  // This ensures data is refreshed when returning from freelancers selection page
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["evaluation_group_freelancers", group.group_id],
    });
    queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
  }, [location.pathname, group.group_id, queryClient]);

  const { data: freelancers = [], isLoading } = useQuery({
    queryKey: ["evaluation_group_freelancers", group.group_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_evaluation_group_freelancers_detail")
        .select("*")
        .eq("group_id", group.group_id)
        .order("freelancer_name");

      if (error) throw error;
      return data as EvaluationGroupFreelancer[];
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const pendingFreelancers = freelancers.filter(
    (f) =>
      f.review_status === 'pending' &&
      f.freelancer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const inReviewFreelancers = freelancers.filter(
    (f) =>
      f.review_status === 'in_review' &&
      f.freelancer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const evaluatedFreelancers = freelancers.filter(
    (f) =>
      f.review_status === 'evaluated' &&
      f.freelancer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEvaluate = (freelancer: EvaluationGroupFreelancer) => {
    navigate(`/avaliacoes/perfil-freelancer/${group.group_id}/avaliar?freelancerId=${freelancer.id}`);
  };

  const handleStartSequential = () => {
    navigate(`/avaliacoes/perfil-freelancer/${group.group_id}/avaliar?mode=sequential`);
  };

  const handleRemoveFreelancer = async () => {
    if (!freelancerToRemove) return;

    setIsRemovingFreelancer(true);
    try {
      const { error } = await supabase
        .from("evaluation_group_freelancers")
        .delete()
        .eq("id", freelancerToRemove.id);

      if (error) throw error;

      toast({
        title: "Freelancer removido",
        description: `${freelancerToRemove.freelancer_name} foi removido do grupo de avaliação.`,
      });

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["evaluation_group_freelancers", group.group_id],
      });
      queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao remover freelancer:", error);
      toast({
        title: "Erro",
        description: "Não foi possível remover o freelancer. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRemovingFreelancer(false);
      setFreelancerToRemove(null);
    }
  };

  const handleSendToReview = (freelancer: EvaluationGroupFreelancer) => {
    setFreelancerToReview(freelancer);
    setReviewReason("");
  };

  const handleConfirmSendToReview = async () => {
    if (!freelancerToReview) return;

    setIsUpdatingReviewStatus(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Usuario nao autenticado");

      const { error } = await supabase.rpc('set_freelancer_in_review', {
        p_evaluation_group_freelancer_id: freelancerToReview.id,
        p_user_id: userData.user.id,
        p_reason: reviewReason || null,
      });

      if (error) throw error;

      // Criar atividade de revisao de cadastro
      await supabase.rpc('create_freelancer_activity', {
        p_freelancer_id: freelancerToReview.freelancer_id,
        p_tipo: 'revisao_cadastro',
        p_assigned_to: null,
        p_observacao: reviewReason || null,
        p_evaluation_group_freelancer_id: freelancerToReview.id,
      });

      toast({
        title: "Enviado para revisao",
        description: `${freelancerToReview.freelancer_name} foi enviado para revisao.`,
      });

      queryClient.invalidateQueries({
        queryKey: ["evaluation_group_freelancers", group.group_id],
      });
      queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao enviar para revisao:", error);
      toast({
        title: "Erro",
        description: "Nao foi possivel enviar para revisao. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingReviewStatus(false);
      setFreelancerToReview(null);
      setReviewReason("");
    }
  };

  const handleBackToPending = async (freelancer: EvaluationGroupFreelancer) => {
    setIsUpdatingReviewStatus(true);
    try {
      const { error } = await supabase.rpc('set_freelancer_pending', {
        p_evaluation_group_freelancer_id: freelancer.id,
      });

      if (error) throw error;

      toast({
        title: "Voltou para pendentes",
        description: `${freelancer.freelancer_name} voltou para a lista de pendentes.`,
      });

      queryClient.invalidateQueries({
        queryKey: ["evaluation_group_freelancers", group.group_id],
      });
      queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao voltar para pendente:", error);
      toast({
        title: "Erro",
        description: "Não foi possível voltar para pendente. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingReviewStatus(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{group.group_name}</h2>
            <p className="text-muted-foreground">Detalhes do grupo de avaliação</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canAddFreelancers && onDeleteGroup && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDeleteGroup}
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              title="Deletar Grupo"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {canAddFreelancers && onAddFreelancers && (
            <Button onClick={onAddFreelancers}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Freelancers
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <CardTitle className="text-xl">Informações Gerais</CardTitle>
              <CardDescription className="text-base">
                {group.group_description || "Sem descrição definida."}
              </CardDescription>
            </div>
            <Badge variant={group.is_active ? "default" : "secondary"}>
              {group.is_active ? "Ativo" : "Inativo"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-2">
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" /> Avaliador
              </span>
              <p className="font-medium leading-none">{group.evaluator_name || "N/A"}</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Criado em
              </span>
              <p className="font-medium leading-none">
                {format(new Date(group.created_at), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" /> Total
              </span>
              <p className="font-medium leading-none">{group.total_freelancers} freelancers</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Em Revisão
              </span>
              <p className="font-medium leading-none">{group.freelancers_in_review || 0} freelancers</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Avaliados
              </span>
              <p className="font-medium leading-none">{group.freelancers_evaluated} freelancers</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Progresso da Avaliação</span>
              <span className="text-muted-foreground">
                {group.progress_percentage}% ({group.freelancers_evaluated + (group.freelancers_in_review || 0)} de {group.total_freelancers})
              </span>
            </div>
            <Progress value={group.progress_percentage} className="h-3" />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            Pendentes ({pendingFreelancers.length})
          </TabsTrigger>
          <TabsTrigger value="in-review">
            Em Revisão ({inReviewFreelancers.length})
          </TabsTrigger>
          <TabsTrigger value="evaluated">
            Avaliados ({evaluatedFreelancers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar freelancer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            {pendingFreelancers.length > 1 && (
              <Button
                variant="default"
                className="gap-2"
                onClick={handleStartSequential}
              >
                <PlayCircle className="h-4 w-4" />
                Avaliar Todos em Sequência
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : pendingFreelancers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? "Nenhum freelancer pendente encontrado."
                : "Nenhum freelancer pendente."}
            </div>
          ) : (
            <div className="grid gap-4">
              {pendingFreelancers.map((freelancer) => (
                <EvaluationFreelancerCard
                  key={freelancer.id}
                  freelancer={freelancer}
                  formId={group.form_id}
                  onEvaluate={handleEvaluate}
                  canRemove={canAddFreelancers}
                  onRemove={setFreelancerToRemove}
                  onSendToReview={handleSendToReview}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="in-review" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar freelancer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : inReviewFreelancers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? "Nenhum freelancer em revisão encontrado."
                : "Nenhum freelancer em revisão."}
            </div>
          ) : (
            <div className="grid gap-4">
              {inReviewFreelancers.map((freelancer) => (
                <EvaluationFreelancerCard
                  key={freelancer.id}
                  freelancer={freelancer}
                  formId={group.form_id}
                  onEvaluate={handleEvaluate}
                  canRemove={canAddFreelancers}
                  onRemove={setFreelancerToRemove}
                  onBackToPending={handleBackToPending}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="evaluated" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar freelancer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : evaluatedFreelancers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? "Nenhum freelancer avaliado encontrado."
                : "Nenhum freelancer foi avaliado ainda."}
            </div>
          ) : (
            <div className="grid gap-4">
              {evaluatedFreelancers.map((freelancer) => (
                <EvaluationFreelancerCard
                  key={freelancer.id}
                  freelancer={freelancer}
                  formId={group.form_id}
                  onEvaluate={handleEvaluate}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Alert Dialog para confirmar remoção de freelancer */}
      <AlertDialog open={!!freelancerToRemove} onOpenChange={(open) => !open && setFreelancerToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover freelancer do grupo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{freelancerToRemove?.freelancer_name}</strong> deste grupo de avaliação?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemovingFreelancer}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFreelancer}
              disabled={isRemovingFreelancer}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemovingFreelancer ? "Removendo..." : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para enviar para revisão com motivo */}
      <Dialog open={!!freelancerToReview} onOpenChange={(open) => !open && setFreelancerToReview(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar para Revisão</DialogTitle>
            <DialogDescription>
              Informe o motivo pelo qual <strong>{freelancerToReview?.freelancer_name}</strong> está sendo enviado para revisão.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="review-reason">Motivo da revisão</Label>
              <Textarea
                id="review-reason"
                placeholder="Ex: Portfólio indisponível, preciso verificar referências..."
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFreelancerToReview(null)}
              disabled={isUpdatingReviewStatus}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSendToReview}
              disabled={isUpdatingReviewStatus}
            >
              {isUpdatingReviewStatus ? "Enviando..." : "Enviar para Revisão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
